<template>
    <main class="relative h-full">
        <img src="../../assets/images/login-Bg.svg" alt="" class="w-full h-screen object-cover">
        <div class="absolute mobile:w-full desktop:w-1/3 top-0 right-0">
            <div
                class="mobile:px-8 tablet:px-32 laptop:px-48 desktop:px-16 largeDesktop:px-28 h-screen flex flex-col items-center justify-center bg-opacity-80 bg-black-100">
                <img src="../../assets/images/fyxify-logo-circle.svg"
                    class="w-24 h-24 md:w-28 md:h-28 largeDesktop:w-40 largeDesktop:h-40 object-cover rounded-[22%] shadow-lg"
                    alt="fyxify Logo">

                <!-- Step 1: Email Input -->
                <div v-if="step === 1">
                    <h4
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-4xl font-medium mobile:my-3 tablet:my-5 md:my-4 largeDesktop:my-10 text-white">
                        Forgot Password?
                    </h4>
                    <p
                        class="mb-4 text-center font-roboto mobile:text-[16px] desktop:text-[18px] largeDesktop:text-[22px] text-white">
                        Enter your email address below and we'll send you instructions
                        on
                        how to change your password!
                    </p>
                    <div class="grid w-full mobile:gap-2 md:gap-4 largeDesktop:gap-5">
                        <div class="form-group">
                            <label for="email" class="text-base font-roboto mb-1 text-white">Email:</label>
                            <inputComponent ref="emailRef" v-model="email" name="email" required
                                :regex="$constants.EMAIL_REGEX" error-message="Please enter a valid email address"
                                placeholder="Enter Email"
                                custom-class="!text-[16px] !py-[8px] largeDesktop:!py-[12px]" />
                        </div>
                    </div>
                    <div class="flex justify-center gap-3 mt-5">
                        <button
                            class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#FE5000] text-[#FE5000] hover:bg-[#FE5000] hover:text-white"
                            @click="backClicked">
                            Cancel
                        </button>
                        <button
                            class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#3D6ECE] bg-gradient-to-r from-[#3D6ECE] to-[#193E89] text-white"
                            @click="goToStep(2)">
                            Submit
                        </button>
                    </div>
                </div>

                <!-- Step 2: OTP Verification -->
                <div v-else-if="step === 2">
                    <h2
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-4xl font-medium mobile:my-3 tablet:my-5 md:my-4 largeDesktop:my-10 text-white">
                        Reset Password
                    </h2>
                    <p
                        class="mb-4 text-center font-roboto mobile:text-[16px] desktop:text-[18px] largeDesktop:text-[22px] text-white">
                        A One Time Pass Code has been sent to your email {{ email }}
                    </p>
                    <div class="grid w-full mobile:gap-2 md:gap-4 largeDesktop:gap-5">
                        <div class="form-group">
                            <label for="email" class="text-base font-roboto mb-1 text-white">Code:</label>
                            <inputComponent ref="codeRef" v-model="otp" placeholder="Enter Code" name="otp" required
                                :regex="/^.{6}$/" error-message="Please enter valid code" :length="6" />
                        </div>
                    </div>
                    <div>
                        <div class="flex justify-center gap-3 mt-5">
                            <button
                                class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#FE5000] text-[#FE5000] hover:bg-[#FE5000] hover:text-white"
                                @click="backClicked">
                                Cancel
                            </button>
                            <button
                                class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#3D6ECE] bg-gradient-to-r from-[#3D6ECE] to-[#193E89] text-white"
                                @click="goToStep(3)">
                                Verify
                            </button>
                        </div>
                        <div class="my-4 text-base text-white">
                            <p class="m-0 text-center">
                                Send the code again
                                <span v-if="timer > 0">({{ timer == 0 ? '' : timer + ' seconds' }})</span>
                            </p>
                        </div>
                        <div class="flex item-center justify-center gap-3">
                            <button :disabled="timer > 0"
                                class="w-max py-2 px-5 rounded-lg text-base disabled:cursor-not-allowed disabled:bg-[#ccc] disabled:!text-gray-600 text-white bg-[#FE5000]"
                                @click="resendCode">
                                Re-send
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Step 3: Set New Password -->
                <div v-else-if="step === 3" class="w-full">
                    <h2
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-4xl font-medium mobile:my-3 tablet:my-5 md:my-4 largeDesktop:my-10 text-white">
                        Reset Password
                    </h2>
                    <div class="grid w-full mobile:gap-2 md:gap-4 largeDesktop:gap-5">
                        <div class="form-group">
                            <label for="password" class="text-base font-roboto  mb-1 text-white">New Password:</label>
                            <div class="relative">
                                <inputComponent ref="passwordRef" v-model="password" name="password" type="password"
                                    required :regex="$constants.PASSWORD_REGEX"
                                    error-message="Minimum 8 characters with uppercase, number & symbol. Emojis are not allowed."
                                    placeholder="Enter Password" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="password" class="text-base font-roboto  mb-1 text-white">Confirm New
                                Password:</label>
                            <div class="relative">
                                <inputComponent ref="confirmPasswordRef" v-model="confirmPassword"
                                    name="confirmPassword" type="password" required :confirm-with="password"
                                    placeholder="Enter Confirm Password" error-message="Passwords do not match" />
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-center gap-3 mt-5">
                        <button
                            class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#FE5000] text-[#FE5000] hover:bg-[#FE5000] hover:text-white"
                            @click="backClicked">
                            Cancel
                        </button>
                        <button
                            class="px-10 py-2 font-roboto font-bold uppercase rounded-full border-[1px] transition duration-300 border-[#193E89] bg-gradient-to-r from-[#193E89] to-[#3D6ECE] text-white"
                            @click="submitNewPassword">
                            Submit
                        </button>
                    </div>
                </div>

                <!-- Step Indicator -->
                <div class="mt-10 flex justify-center space-x-2">
                    <span v-for="n in 3" :key="n" class="h-2 w-2 rounded-full"
                        :class="step === n ? 'w-8 bg-[#FE5000]' : 'bg-white'" />
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import inputComponent from '../../components/partialComponent/inputComponent.vue';
import { useToast } from "vue-toastification";
import { mapActions, mapGetters } from "vuex";
const toast = useToast();
export default {
    components: {
        inputComponent,
    },
    data() {
        return {
            step: 1,
            email: '',
            otp: '',
            password: '',
            confirmPassword: '',
            showPassword: false,
            timer: 30,
            interval: null,
        }
    },
    computed: {
        ...mapGetters([
            "resetAuthResponse",
            "resetResendCodeResponse",
            "resetVerifyResponse",
            "resetPasswordResponse"
        ]),
    },

    watch: {
        resetAuthResponse: function (data) {
            toast.success(this.resetAuthResponse.message);
            this.timer = 30;
            this.startTimer();
            this.step = 2
        },
        resetResendCodeResponse: function (data) {
            toast.success(this.resetResendCodeResponse.message);
            this.timer = 30;
            this.startTimer();
        },
        resetVerifyResponse: function (data) {
            this.step = 3
        },
        resetPasswordResponse: function (data) {
            toast.success(this.resetPasswordResponse.message);
            this.$router.go(-1)
        }
    },

    mounted() {
        if (this.step == '2') {
            this.startTimer();
        }

    },
    unmounted() {
        if (this.interval) clearInterval(this.interval);
    },

    methods: {
        ...mapActions([
            "resetAuth",
            "resetResendCode",
            "resetVerify",
            "resetPassword"
        ]),
        goToStep(step) {
            if (step == "2") {
                this.authenticateClicked()
            } else if (step == "3") {
                this.verifyClicked()
            } else {
                this.step = step
            }
        },

        backClicked() {
            this.$router.go(-1)
        },
        authenticateClicked() {
            let ref = this.$refs.emailRef
            if (ref?.validate?.()) {
                return
            }
            this.resetAuth({
                email: this.email.trim()
            })
        },
        verifyClicked() {
            let ref = this.$refs.codeRef
            if (ref?.validate?.()) {
                return
            }
            this.resetVerify({
                email: this.email.trim(),
                code: this.otp
            })
        },
        resendCode() {
            this.resetResendCode({
                email: this.email.trim()
            })
        },
        submitNewPassword() {
            let passwordRef = this.$refs.passwordRef
            let confirmPasswordRef = this.$refs.confirmPasswordRef

            if (passwordRef?.validate?.() || confirmPasswordRef?.validate?.()) {
                return
            }

            this.resetPassword({
                password: this.password.trim(),
                password_confirmation: this.confirmPassword,
                temp_token: this.resetVerifyResponse.temp_token
            })
        },
        startTimer() {
            if (this.interval) clearInterval(this.interval);
            this.interval = setInterval(() => {
                if (this.timer > 0) {
                    this.timer--;
                } else {
                    clearInterval(this.interval);
                }
            }, 1000);
        },
    }

};
</script>
